import { useEffect, useRef, useCallback, useState } from 'react';
import Video, { Room, LocalParticipant } from 'twilio-video';
import useVideoContext from '../useVideoContext/useVideoContext';
import useRoomStats from '../useRoomStats/useRoomStats';

// Issue types that can be detected (only error-level issues)
export type IssueType =
  | 'very_low_quality'      // Network quality level 0-1
  | 'reconnecting'          // Room entering reconnecting state
  | 'signaling_error'       // Signaling connection error
  | 'media_connection_failed' // Media connection issues
  | 'track_publication_failed' // Failed to publish track
  | 'audio_denied'          // Audio permission denied
  | 'video_denied';         // Video permission denied

export interface DetectedIssue {
  type: IssueType;
  severity: 'warning' | 'error';
  description: string;
  timestamp: number;
  details?: Record<string, any>;
}

export interface IssueReport {
  participantSid: string;
  participantIdentity: string;
  meetingId: string;
  networkIssue: string | null;
  issueReason: string | null;
  audioPermission: boolean | null;
  videoPermission: boolean | null;
  detectedIssues: DetectedIssue[];
  networkQualityLevel: number | null;
  stats: {
    packetLoss: number;
    latency: number;
    jitter: number;
    bitrate: number;
  } | null;
  timestamp: number;
}

// Thresholds for issue detection
const THRESHOLDS = {
  packetLoss: 5,        // percentage
  latency: 400,         // ms (RTT)
  jitter: 100,          // ms
  lowBandwidth: 100,    // kbps
  qualityWarning: 2,    // network quality level
  qualityCritical: 1,   // network quality level
};

// Debounce time before sending issue report (to aggregate issues)
const REPORT_DEBOUNCE_MS = 5000;

// Minimum interval between reports for the same participant
const MIN_REPORT_INTERVAL_MS = 30000;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

async function sendIssueReport(report: IssueReport): Promise<void> {
  const apiKey = window.sessionStorage.getItem('m_key') || '';

  // Build the network issue string from detected issues
  const networkIssueTypes = report.detectedIssues
    .filter(i => ['very_low_quality', 'low_quality', 'high_packet_loss', 'high_latency', 'high_jitter', 'low_bandwidth', 'reconnecting'].includes(i.type))
    .map(i => i.type);
  
  // Deduplicate issue types
  const uniqueIssueTypes = Array.from(new Set(networkIssueTypes));
  
  const networkIssue = uniqueIssueTypes.length > 0 ? uniqueIssueTypes.join(', ') : null;
  
  // Build human-readable issue reason
  const issueDescriptions = report.detectedIssues.map(i => i.description);
  const uniqueDescriptions = Array.from(new Set(issueDescriptions));
  const issueReason = uniqueDescriptions.length > 0 ? uniqueDescriptions.join('; ') : null;

  // Determine permissions
  const audioPermission = report.audioPermission !== false;
  const videoPermission = report.videoPermission !== false;

  try {
    const response = await fetch(`${API_BASE_URL}/internal/add-reason`, {
      method: 'PUT',
      body: JSON.stringify({
        sid: report.participantSid,
        network_issue: networkIssue,
        issue_reason: issueReason,
        audio_permission: audioPermission,
        video_permission: videoPermission,
        // Extended data for future dashboard enhancements
        detected_issues: report.detectedIssues,
        network_quality_level: report.networkQualityLevel,
        stats: report.stats,
        timestamp: new Date(report.timestamp).toISOString(),
      }),
      headers: {
        'content-type': 'application/json',
        'X-API-KEY': apiKey,
      },
    });

    if (!response.ok) {
      console.warn('[IssueReporter] Failed to send issue report:', response.status);
    } else {
      console.log('[IssueReporter] Issue report sent successfully:', {
        issues: networkIssueTypes,
        reason: issueReason,
      });
    }
  } catch (error) {
    console.error('[IssueReporter] Error sending issue report:', error);
  }
}

// Get the Twilio Video JavaScript Logger
function getTwilioLogger() {
  try {
    const { Logger } = Video;
    return Logger.getLogger('twilio-video');
  } catch (e) {
    console.warn('[IssueReporter] Could not access Twilio Video Logger:', e);
    return null;
  }
}

export default function useIssueReporter(enabled: boolean = true) {
  const { room } = useVideoContext();
  const roomStats = useRoomStats(5000); // Collect stats every 5 seconds
  
  const [detectedIssues, setDetectedIssues] = useState<DetectedIssue[]>([]);
  const [audioPermission, setAudioPermission] = useState<boolean | null>(null);
  const [videoPermission, setVideoPermission] = useState<boolean | null>(null);
  const [networkQualityLevel, setNetworkQualityLevel] = useState<number | null>(null);
  
  const lastReportTimeRef = useRef<number>(0);
  const reportTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const issuesRef = useRef<DetectedIssue[]>([]);
  const loggerSetupRef = useRef<boolean>(false);
  const originalMethodFactoryRef = useRef<any>(null);

  // Add an issue to the list (deduplicating by type)
  const addIssue = useCallback((issue: DetectedIssue) => {
    setDetectedIssues((prev: DetectedIssue[]) => {
      // Check if issue of same type already exists within last 30 seconds
      const existing = prev.find((i: DetectedIssue) => i.type === issue.type && (Date.now() - i.timestamp) < 30000);
      if (existing) {
        return prev;
      }
      const updated = [...prev, issue];
      issuesRef.current = updated;
      return updated;
    });
  }, []);

  // Remove an issue by type
  const removeIssue = useCallback((type: IssueType) => {
    setDetectedIssues((prev: DetectedIssue[]) => {
      const updated = prev.filter((i: DetectedIssue) => i.type !== type);
      issuesRef.current = updated;
      return updated;
    });
  }, []);

  // Send aggregated issue report
  const sendReport = useCallback(async () => {
    // Only send report if there are actual error-level issues (not just warnings)
    const errorIssues = issuesRef.current.filter((i: DetectedIssue) => i.severity === 'error');
    if (!room || !enabled || errorIssues.length === 0) return;

    const now = Date.now();
    
    // Check minimum interval
    if (now - lastReportTimeRef.current < MIN_REPORT_INTERVAL_MS) {
      return;
    }

    lastReportTimeRef.current = now;

    const report: IssueReport = {
      participantSid: room.localParticipant.sid,
      participantIdentity: room.localParticipant.identity,
      meetingId: room.name,
      networkIssue: null, // Will be filled by sendIssueReport
      issueReason: null,  // Will be filled by sendIssueReport
      audioPermission,
      videoPermission,
      detectedIssues: errorIssues, // Only report error-level issues
      networkQualityLevel,
      stats: roomStats ? {
        packetLoss: roomStats.aggregate.avgPacketLoss,
        latency: roomStats.aggregate.avgRoundTripTime,
        jitter: roomStats.aggregate.avgJitter,
        bitrate: roomStats.aggregate.totalBitrate,
      } : null,
      timestamp: now,
    };

    await sendIssueReport(report);
  }, [room, enabled, audioPermission, videoPermission, networkQualityLevel, roomStats]);

  // Schedule a debounced report
  const scheduleReport = useCallback(() => {
    if (reportTimeoutRef.current) {
      clearTimeout(reportTimeoutRef.current);
    }
    reportTimeoutRef.current = setTimeout(() => {
      sendReport();
    }, REPORT_DEBOUNCE_MS);
  }, [sendReport]);

  // Monitor network quality level
  useEffect(() => {
    if (!room || !enabled) return;

    const localParticipant = room.localParticipant;

    const handleQualityChange = (level: number) => {
      console.log('[IssueReporter] Network quality changed:', level);
      setNetworkQualityLevel(level);

      if (level <= THRESHOLDS.qualityCritical) {
        addIssue({
          type: 'very_low_quality',
          severity: 'error',
          description: `Very poor network quality (level ${level}/5). Connection may be unstable.`,
          timestamp: Date.now(),
          details: { level },
        });
        scheduleReport();
      } else {
        // Quality recovered or is acceptable (level 2+ is not considered an error)
        removeIssue('very_low_quality');
      }
    };

    // Set initial value
    if (localParticipant.networkQualityLevel !== null) {
      handleQualityChange(localParticipant.networkQualityLevel);
    }

    localParticipant.on('networkQualityLevelChanged', handleQualityChange);
    return () => {
      localParticipant.off('networkQualityLevelChanged', handleQualityChange);
    };
  }, [room, enabled, addIssue, removeIssue, scheduleReport]);

  // Note: Room stats (packet loss, latency, jitter, bandwidth) are still collected via useRoomStats
  // and included in error reports for diagnostic purposes, but we no longer flag them as issues
  // since they are common/transient and were causing too many false positives.

  // Monitor room state (reconnecting)
  useEffect(() => {
    if (!room || !enabled) return;

    const handleReconnecting = () => {
      console.log('[IssueReporter] Room reconnecting');
      addIssue({
        type: 'reconnecting',
        severity: 'error',
        description: 'Connection lost. Attempting to reconnect...',
        timestamp: Date.now(),
      });
      scheduleReport();
    };

    const handleReconnected = () => {
      console.log('[IssueReporter] Room reconnected');
      removeIssue('reconnecting');
    };

    room.on('reconnecting', handleReconnecting);
    room.on('reconnected', handleReconnected);

    return () => {
      room.off('reconnecting', handleReconnecting);
      room.off('reconnected', handleReconnected);
    };
  }, [room, enabled, addIssue, removeIssue, scheduleReport]);

  // Monitor track publication failures
  useEffect(() => {
    if (!room || !enabled) return;

    const handleTrackPublicationFailed = (error: Error) => {
      console.log('[IssueReporter] Track publication failed:', error);
      addIssue({
        type: 'track_publication_failed',
        severity: 'error',
        description: `Failed to publish track: ${error.message}`,
        timestamp: Date.now(),
        details: { error: error.message },
      });
      scheduleReport();
    };

    room.localParticipant.on('trackPublicationFailed', handleTrackPublicationFailed);

    return () => {
      room.localParticipant.off('trackPublicationFailed', handleTrackPublicationFailed);
    };
  }, [room, enabled, addIssue, scheduleReport]);

  // Setup JavaScript Logger to intercept SDK logs
  useEffect(() => {
    if (!enabled || loggerSetupRef.current) return;

    const logger = getTwilioLogger();
    if (!logger) return;

    loggerSetupRef.current = true;
    originalMethodFactoryRef.current = logger.methodFactory;

    logger.methodFactory = function(methodName: string, logLevel: number, loggerName: string | symbol) {
      const originalMethod = originalMethodFactoryRef.current(methodName, logLevel, loggerName);
      
      return function(datetime: string, level: string, component: string, message: string, data?: any) {
        // Call original method first
        originalMethod(datetime, level, component, message, data);

        // Intercept signaling errors only (not informational events like 'waiting')
        if (message === 'event' && data?.group === 'signaling') {
          if (data.name === 'closed' && data.level === 'error') {
            console.log('[IssueReporter] Signaling connection error:', data.payload?.reason);
            addIssue({
              type: 'signaling_error',
              severity: 'error',
              description: `Signaling connection error: ${data.payload?.reason || 'Unknown error'}`,
              timestamp: Date.now(),
              details: { reason: data.payload?.reason },
            });
            scheduleReport();
          }
        }

        // Intercept media connection failures
        if (message === 'event' && data?.group === 'media' && data?.level === 'error') {
          console.log('[IssueReporter] Media connection error:', data);
          addIssue({
            type: 'media_connection_failed',
            severity: 'error',
            description: 'Media connection failed. Audio/video may not work.',
            timestamp: Date.now(),
            details: data,
          });
          scheduleReport();
        }
      };
    };

    // Set log level to warn to only capture warnings and errors (not debug/info noise)
    logger.setLevel('warn');

    console.log('[IssueReporter] Twilio Video Logger configured for issue detection');

    return () => {
      // Restore original method factory
      if (originalMethodFactoryRef.current && logger) {
        logger.methodFactory = originalMethodFactoryRef.current;
        loggerSetupRef.current = false;
      }
    };
  }, [enabled, addIssue, scheduleReport]);

  // Track permissions
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasAudio = devices.some(d => d.kind === 'audioinput' && d.label);
        const hasVideo = devices.some(d => d.kind === 'videoinput' && d.label);
        
        setAudioPermission(hasAudio);
        setVideoPermission(hasVideo);

        // We don't add these to detectedIssues anymore because they are handled 
        // by the dedicated permission flags and UI section.
      } catch (error) {
        console.error('[IssueReporter] Error checking permissions:', error);
      }
    };

    if (enabled) {
      checkPermissions();
    }
  }, [enabled, addIssue]);

  // Send final report on disconnect
  useEffect(() => {
    if (!room || !enabled) return;

    const handleDisconnect = () => {
      console.log('[IssueReporter] Room disconnected, sending final report');
      // Send immediately without debounce
      if (issuesRef.current.length > 0) {
        sendReport();
      }
    };

    room.on('disconnected', handleDisconnect);
    return () => {
      room.off('disconnected', handleDisconnect);
    };
  }, [room, enabled, sendReport]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (reportTimeoutRef.current) {
        clearTimeout(reportTimeoutRef.current);
      }
    };
  }, []);

  return {
    detectedIssues,
    networkQualityLevel,
    audioPermission,
    videoPermission,
    // Expose for manual triggering if needed
    sendReport,
  };
}
